<!--
  QaAuditCalls.vue — Care Management > QA > Audit calls: the calls a manager can audit by hand against the same
  configured rubrics, filtered by team, care manager, program, patient and date.
-->
<template>
  <div style="flex:1;min-height:0;padding:2px 16px 16px;">
      <div class="cm-panel" style="height:100%;display:flex;flex-direction:column;overflow:hidden;">
        <div class="flex items-end gap-3 px-4 py-3 border-b border-gray-200 flex-wrap">
          <FilterDropdown v-if="!filtersReady || teams.length" class="w-48 shrink-0" full-width :model-value="auditTeamFilter" :options="teamOptions" placeholder="All teams" label="Team" clearable searchable :loading="!filtersReady" data-test="audit-team-filter" @update:model-value="setAuditTeam" />
          <FilterDropdown class="w-56 shrink-0" full-width :model-value="auditCmFilter" :options="auditCmOptions" placeholder="All care managers" label="Care manager" clearable searchable sensitive-labels :loading="!filtersReady" data-test="audit-cm-filter" @update:model-value="setAuditCm" />
          <FilterDropdown class="w-56 shrink-0" full-width :model-value="auditProgramFilter" :options="auditProgramOptions" :placeholder="`All ${opportunityNounPlural.toLowerCase()}`" :label="opportunityNoun" clearable searchable :loading="!filtersReady" data-test="audit-program-filter" @update:model-value="setAuditProgram" />
          <label class="flex flex-col shrink-0"><span class="text-xs font-semibold mb-0.5">Patient</span><input v-model="auditSearch" class="sensitive w-52 h-[34px] px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary-500" type="text" placeholder="Search by name…" data-test="audit-search" @input="debouncedSearch" /></label>
          <label class="flex flex-col shrink-0"><span class="text-xs font-semibold mb-0.5">From</span><input v-model="auditDateFrom" class="w-36 h-[34px] px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary-500" type="date" @change="auditQueueOffset = 0; loadAuditQueue()" /></label>
          <label class="flex flex-col shrink-0"><span class="text-xs font-semibold mb-0.5">To</span><input v-model="auditDateTo" class="w-36 h-[34px] px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary-500" type="date" @change="auditQueueOffset = 0; loadAuditQueue()" /></label>
          <div class="flex items-center gap-1 ml-auto h-[34px]">
            <button type="button" :disabled="!hasAuditFilters" :class="['px-2 py-1.5 text-xs whitespace-nowrap', hasAuditFilters ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed']" data-test="audit-clear-filters" @click="clearAuditFilters"><FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />Clear Filters</button>
            <button type="button" :disabled="!isAuditFilterDirty" :class="['inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors whitespace-nowrap', isAuditFilterDirty ? 'border-primary-500 bg-primary-500 text-white hover:opacity-90' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed']" data-test="audit-save-filters" @click="saveAuditFilters"><FontAwesomeIcon :icon="['fas', 'floppy-disk']" class="text-[10px]" />Save Filters</button>
            <button type="button" :disabled="auditQueueLoading" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" data-test="audit-refresh" @click="auditQueueOffset = 0; loadAuditQueue()"><FontAwesomeIcon :icon="['fas', 'arrows-rotate']" class="text-[10px]" :class="{ 'animate-spin': auditQueueLoading }" />Refresh</button>
          </div>
        </div>
        <div class="qa-scroll" style="flex:1;min-height:0;overflow:auto;">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 sticky top-0 z-[1]">
              <tr>
                <th class="px-4 py-3"><span class="sr-only">Actions</span></th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Team member</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Patient</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ opportunityNoun }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Call type</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style="text-align:right;">Duration</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <template v-if="auditQueueLoading">
                <tr v-for="n in auditQueueLimit" :key="`skeleton-${n}`" data-test="audit-skeleton-row">
                  <td class="px-4 py-3"><div class="h-6 w-28 bg-gray-200 rounded-md animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-24 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-36 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-10 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                </tr>
              </template>
              <tr v-else-if="queueLoaded && !auditQueue.length">
                <td colspan="7" class="px-4 py-10 text-center text-sm text-gray-500" data-test="audit-empty">{{ hasAuditFilters ? 'No calls match these filters.' : 'No calls to audit.' }}</td>
              </tr>
              <tr v-for="call in auditQueue" v-else :key="call.call_log_uuid || `work-${call.work_id}`" class="hover:bg-gray-50">
                <td class="px-4 py-3 whitespace-nowrap">
                  <button type="button" class="mr-1.5 inline-flex items-center justify-center w-20 px-2.5 py-1 text-xs font-medium rounded-md border border-primary-500 bg-primary-500 text-white hover:opacity-90" data-test="audit-start" @click="startHumanAudit(call)">{{ call.audit_count ? 'Re-audit' : 'Audit' }}</button>
                  <router-link v-if="call.work_id" class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50" style="text-decoration:none;" :to="sessionLocation(call.work_id)" target="_blank" data-test="audit-open-session">Open session</router-link>
                </td>
                <td class="px-4 py-3 text-sm text-gray-700" style="white-space:nowrap;">{{ fmtDate(call.engagement_at) }}</td>
                <td class="px-4 py-3 text-sm text-gray-700"><span class="sensitive">{{ call.care_manager_name }}</span></td>
                <td class="px-4 py-3 text-sm text-gray-700"><span class="sensitive font-medium text-gray-900">{{ call.patient_name || 'Unlinked call' }}</span></td>
                <td class="px-4 py-3 text-sm text-gray-700">{{ call.opportunity_type || '—' }}</td>
                <td class="px-4 py-3 text-sm text-gray-700">{{ call.task_type_name || '—' }}</td>
                <td class="px-4 py-3 text-sm text-gray-700" style="text-align:right;font-variant-numeric:tabular-nums;">{{ fmtDuration(call.duration_seconds) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="queueLoaded && auditQueueCount" class="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 text-sm text-gray-500">
          <span>Showing {{ (auditQueueOffset + 1).toLocaleString() }}–{{ Math.min(auditQueueOffset + auditQueueLimit, auditQueueCount).toLocaleString() }} of {{ auditQueueCount.toLocaleString() }} calls</span>
          <div v-if="auditQueueCount > auditQueueLimit" class="flex gap-1.5">
            <button type="button" class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" :disabled="auditQueueLoading || auditQueueOffset === 0" @click="auditQueueOffset -= auditQueueLimit; loadAuditQueue()">Previous</button>
            <button type="button" class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" :disabled="auditQueueLoading || auditQueueOffset + auditQueueLimit >= auditQueueCount" @click="auditQueueOffset += auditQueueLimit; loadAuditQueue()">Next</button>
          </div>
        </div>
      </div>

    <teleport to="body">
      <div v-if="humanAuditCall" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" @click.self="closeHumanAudit">
        <div class="bg-white rounded-lg border border-gray-200 w-[60rem] max-w-[95vw] max-h-[92vh] flex flex-col">
          <div class="px-4 py-3 border-b border-gray-100 flex items-center"><div><h3 class="text-sm font-semibold text-gray-900">{{ humanAuditCall.audit_count ? 'Re-audit' : 'Audit' }} call</h3><p class="text-xs text-gray-500 sensitive">{{ humanAuditCall.care_manager_name }} · {{ humanAuditCall.patient_name }}</p></div><button class="ml-auto text-gray-400" @click="closeHumanAudit">×</button></div>
          <div class="p-4 overflow-y-auto">
            <div v-if="humanAuditCall.has_recordings" style="margin-bottom:12px;padding:10px;border:1px solid #E8E9E9;border-radius:6px;background:#F8F8F9;">
              <div style="font-size: 0.86em;font-weight:600;color:#6B7280;margin-bottom:6px;">Call recording</div>
              <audio v-if="humanRecordingUrl && isRecordingFor(humanAuditCall)" :src="humanRecordingUrl" controls style="width:100%;height:32px;"></audio>
              <button v-else class="qa-back" :disabled="humanRecordingLoading" @click="loadRecording(humanAuditCall)">
                {{ humanRecordingLoading ? 'Preparing audio…' : 'Play audio' }}
              </button>
              <p v-if="recordingError && isRecordingFor(humanAuditCall)" style="color:#B91C1C;margin-top:6px;" data-test="audit-audio-error">{{ recordingError }}</p>
            </div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Audit type</label>
            <select v-model.number="humanAudit.template_id" class="qa-sort" style="max-width:none;width:100%;padding:6px;margin-bottom:12px;" @change="resetHumanAnswers"><option :value="null">Select a team rubric…</option><option v-for="r in rubrics" :key="r.id" :value="r.id">{{ r.rubric_label }}</option></select>
            <p v-if="activeHumanRubric?.disclaimer" style="color:#6B7280;margin:-6px 0 12px;">{{ activeHumanRubric.disclaimer }}</p>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;">
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Opportunity being reviewed</label>
                <select v-model.number="humanAudit.opportunity_id" class="qa-sort" style="max-width:none;width:100%;padding:6px;" :disabled="!humanOpportunities.length">
                  <option :value="null">{{ humanOpportunities.length ? 'Select an opportunity…' : 'No opportunities linked to this call' }}</option>
                  <option v-for="o in humanOpportunities" :key="o.id" :value="o.id">{{ o.type || 'Opportunity' }} (#{{ o.id }})</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Opportunity type</label>
                <div class="qa-sort" style="max-width:none;width:100%;padding:6px;background:#F8F8F9;">{{ humanReviewedOpportunity?.type || '—' }}</div>
              </div>
            </div>
            <div v-if="humanAuditCall.audit_count" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;"><select v-model="humanAudit.re_audit_reason" class="qa-sort" style="max-width:none;padding:6px;"><option value="">Re-audit reason…</option><option value="new_information">New information discovered</option><option value="qa_dispute">QA dispute</option><option value="other">Other</option></select><input v-model="humanAudit.re_audit_note" class="qa-sort" style="max-width:none;padding:6px;" placeholder="Optional note" /></div>
            <QaRubric v-model:answers="humanAudit.answers" mode="answer" :questions="humanQuestions" />
            <label class="block text-xs font-medium text-gray-600 mb-1" style="margin-top:12px;">Review notes / follow-up</label>
            <textarea v-model="humanAudit.review_notes" class="qa-sort" rows="3" style="max-width:none;width:100%;padding:6px;" placeholder="Optional"></textarea>
            <p v-if="humanAuditError" style="color:#B91C1C;margin-top:8px;">{{ humanAuditError }}</p>
          </div>
          <div class="px-4 py-3 border-t border-gray-100 flex justify-end gap-2"><button class="qa-back" @click="closeHumanAudit">Cancel</button><button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="!humanAuditComplete || humanAuditSaving" @click="submitHumanAudit">{{ humanAuditSaving ? 'Submitting…' : 'Submit audit' }}</button></div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import { z } from 'zod';
import isEqual from 'lodash/isEqual';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import { BrowserStore } from '@/utils/BrowserStore';
import { listRubrics, listAuditQueue, listAuditCareManagers, listCalls, submitAudit } from '@/services/api/cmQaApi';
import { qaAuditLocation } from '../../../router/caremanagement_routes';
import QaRubric from './QaRubric.vue';
import qaRecordingMixin from './qaRecordingMixin';
import { fmtDate, fmtDuration, sessionLocation } from './qaFormat';

// Saved filters live in this browser (like the CM Patients list); the report has its own key.
const auditFilterStore = new BrowserStore(
  'cm_qa_audit_saved_filters',
  z.object({
    auditTeamFilter: z.number().nullable().default(null),
    auditCmFilter: z.number().nullable().default(null),
    auditProgramFilter: z.number().nullable().default(null),
    auditSearch: z.string().default(''),
    auditDateFrom: z.string().default(''),
    auditDateTo: z.string().default(''),
  }),
);

export default {
  name: 'QaAuditCalls',
  components: { FilterDropdown, FontAwesomeIcon, QaRubric },
  mixins: [qaRecordingMixin],
  props: {
    sessionId: { type: String, default: null },
  },
  data() {
    const saved = auditFilterStore.load();
    return {
      auditQueue: [],
      auditQueueCount: 0,
      auditQueueOffset: 0,
      auditQueueLimit: 20,
      // True from the start, so the table shows its skeleton before the first request goes out.
      auditQueueLoading: true,
      // Set once a load has answered: the empty message and the footer wait for it.
      queueLoaded: false,
      auditSearch: saved.auditSearch,
      auditCmFilter: saved.auditCmFilter,
      auditTeamFilter: saved.auditTeamFilter,
      auditProgramFilter: saved.auditProgramFilter,
      auditDateFrom: saved.auditDateFrom,
      auditDateTo: saved.auditDateTo,
      savedAuditFilters: auditFilterStore.hasSavedData() ? saved : null,
      // The filters' options (everyone with a call in the queue, their teams, the programs): skeletons until loaded.
      careManagers: [],
      teams: [],
      programs: [],
      filtersReady: false,
      rubrics: [],
      humanAuditCall: null,
      humanAudit: { template_id: null, answers: [], re_audit_reason: '', re_audit_note: '', opportunity_id: null, review_notes: '' },
      humanAuditSaving: false,
      humanAuditError: '',
    };
  },
  computed: {
    ...mapGetters('tenant', ['opportunityNoun', 'opportunityNounPlural']),
    activeHumanRubric() { return this.rubrics.find(r => r.id === this.humanAudit.template_id); },
    humanOpportunities() { return this.humanAuditCall?.opportunities || []; },
    humanReviewedOpportunity() { return this.humanOpportunities.find(o => o.id === this.humanAudit.opportunity_id) || null; },
    humanQuestions() { return this.activeHumanRubric?.questions || []; },
    humanAuditComplete() {
      return !!this.humanQuestions.length
        && this.humanAudit.answers.length === this.humanQuestions.length
        && this.humanAudit.answers.every(a => a.answer)
        && (!this.humanAuditCall?.audit_count || this.humanAudit.re_audit_reason);
    },
    teamOptions() {
      return this.teams.map((t) => ({ value: t.id, label: t.name }));
    },
    // Everyone with a call in the queue (graded or not), narrowed to the team.
    auditCmOptions() {
      return this.careManagers
        .filter((cm) => !this.auditTeamFilter || cm.team_ids.includes(this.auditTeamFilter))
        .map((cm) => ({ value: cm.care_manager_user_id, label: cm.care_manager_name }));
    },
    auditProgramOptions() {
      return this.programs.map((p) => ({ value: p.id, label: p.name }));
    },
    auditFilterState() {
      return {
        auditTeamFilter: this.auditTeamFilter, auditCmFilter: this.auditCmFilter, auditProgramFilter: this.auditProgramFilter,
        auditSearch: this.auditSearch, auditDateFrom: this.auditDateFrom, auditDateTo: this.auditDateTo,
      };
    },
    hasAuditFilters() {
      return Object.values(this.auditFilterState).some(Boolean);
    },
    isAuditFilterDirty() {
      if (this.savedAuditFilters === null) return this.hasAuditFilters;
      return !isEqual(this.auditFilterState, this.savedAuditFilters);
    },
  },
  watch: {
    sessionId(val) {
      if (val && this.auditQueue.length) this.openSession(val);
      else if (!val && this.humanAuditCall) this.humanAuditCall = null;
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    }, 300);
  },
  async mounted() {
    // The queue loads alongside its filters' options. Only a saved team, care manager or program waits for the
    // options, so one that no longer exists is dropped before the queue is asked for it.
    const options = Promise.all([this.loadFilterOptions(), this.loadRubrics()]).then(() => { this.filtersReady = true; });
    if (this.auditTeamFilter || this.auditCmFilter || this.auditProgramFilter) await options;
    await this.loadAuditQueue();
    if (this.sessionId) {
      await options; // the audit form needs the rubrics
      this.openSession(this.sessionId);
    }
  },
  beforeUnmount() {
    if (this.debouncedSearch) this.debouncedSearch.cancel();
  },
  methods: {
    fmtDate,
    fmtDuration,
    sessionLocation,
    async loadFilterOptions() {
      try {
        const data = await listAuditCareManagers();
        this.careManagers = data.care_managers || [];
        this.teams = data.teams || [];
        this.programs = data.programs || [];
        // A saved team, care manager or program that is no longer in the queue would filter to nothing unseen.
        if (this.auditTeamFilter && !this.teams.some((t) => t.id === this.auditTeamFilter)) this.auditTeamFilter = null;
        if (this.auditCmFilter && !this.auditCmOptions.some((o) => o.value === this.auditCmFilter)) this.auditCmFilter = null;
        if (this.auditProgramFilter && !this.programs.some((p) => p.id === this.auditProgramFilter)) this.auditProgramFilter = null;
      } catch (e) {
        console.error('Failed to load QA care managers:', e);
      }
    },
    async loadRubrics() {
      try {
        this.rubrics = (await listRubrics()).results || [];
      } catch (e) {
        console.error('Failed to load QA rubrics:', e);
      }
    },
    setAuditTeam(teamId) {
      this.auditTeamFilter = teamId;
      if (this.auditCmFilter && !this.auditCmOptions.some((o) => o.value === this.auditCmFilter)) this.auditCmFilter = null;
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    },
    setAuditCm(userId) {
      this.auditCmFilter = userId;
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    },
    setAuditProgram(programId) {
      this.auditProgramFilter = programId;
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    },
    saveAuditFilters() {
      const state = { ...this.auditFilterState };
      auditFilterStore.save(state);
      this.savedAuditFilters = state;
    },
    clearAuditFilters() {
      Object.assign(this, {
        auditTeamFilter: null, auditCmFilter: null, auditProgramFilter: null, auditSearch: '', auditDateFrom: '', auditDateTo: '',
      });
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    },
    async loadAuditQueue() {
      this.auditQueueLoading = true;
      // Only the latest request fills the table (typing in search fires several).
      const request = (this.queueRequest || 0) + 1;
      this.queueRequest = request;
      try {
        const filters = {};
        if (this.auditSearch) filters.search = this.auditSearch;
        if (this.auditCmFilter) filters.care_manager_user_id = this.auditCmFilter;
        if (this.auditTeamFilter) filters.team_id = this.auditTeamFilter;
        if (this.auditProgramFilter) filters.program_id = this.auditProgramFilter;
        if (this.auditDateFrom) filters.date_from = this.auditDateFrom;
        if (this.auditDateTo) filters.date_to = this.auditDateTo;
        const data = await listAuditQueue(filters, { limit: this.auditQueueLimit, offset: this.auditQueueOffset });
        if (request !== this.queueRequest) return;
        this.auditQueue = data.results || [];
        this.auditQueueCount = data.count || 0;
        this.queueLoaded = true;
      } catch (e) {
        if (request === this.queueRequest) console.error('Failed to load the audit queue:', e);
      } finally {
        if (request === this.queueRequest) this.auditQueueLoading = false;
      }
    },
    openSession(sessionId) {
      const call = this.auditQueue.find(
        (c) => c.call_log_uuid === sessionId || (c.work_id && String(c.work_id) === String(sessionId)),
      );
      if (call) this.startHumanAudit(call, { updateUrl: false });
    },
    startHumanAudit(call, { updateUrl = true } = {}) {
      this.resetRecording();
      this.humanAuditCall = call;
      this.humanAuditError = '';
      const opportunities = call.opportunities || [];
      this.humanAudit = {
        template_id: this.rubrics[0]?.id || null, answers: [], re_audit_reason: '', re_audit_note: '',
        opportunity_id: opportunities.length === 1 ? opportunities[0].id : null, review_notes: '',
      };
      this.resetHumanAnswers();
      if (updateUrl) {
        const key = call.call_log_uuid || call.uuid || (call.work_id ? String(call.work_id) : null);
        this.$router.push(qaAuditLocation(key)).catch(() => {});
      }
    },
    closeHumanAudit() {
      this.resetRecording();
      this.humanAuditCall = null;
      this.$router.push(qaAuditLocation()).catch(() => {});
    },
    resetHumanAnswers() {
      this.humanAudit.answers = this.humanQuestions.map(q => ({ id: q.id, answer: '', quote: '', rationale: '' }));
    },
    async submitHumanAudit() {
      this.humanAuditSaving = true;
      this.humanAuditError = '';
      try {
        let original = null;
        if (this.humanAuditCall.audit_count) {
          const calls = await listCalls(this.humanAuditCall.care_manager_user_id);
          original = (calls.results || []).find(
            (c) => c.source === 'human' && (
              (this.humanAuditCall.call_log_uuid && c.call_log_uuid === this.humanAuditCall.call_log_uuid)
              || (this.humanAuditCall.work_id && String(c.work_id) === String(this.humanAuditCall.work_id))
            ),
          );
        }
        await submitAudit({
          call_log_uuid: this.humanAuditCall.call_log_uuid || null,
          work_id: this.humanAuditCall.work_id || null,
          template_id: this.humanAudit.template_id,
          answers: this.humanAudit.answers,
          re_audit_of_id: original?.analyst_job_id || null,
          re_audit_reason: this.humanAudit.re_audit_reason,
          re_audit_note: this.humanAudit.re_audit_note,
          opportunity_id: this.humanAudit.opportunity_id,
          review_notes: this.humanAudit.review_notes,
        });
        this.humanAuditCall = null;
        this.$router.push(qaAuditLocation()).catch(() => {});
        await this.loadAuditQueue();
      } catch (e) {
        this.humanAuditError = e?.response?.data?.detail || 'Could not submit audit.';
      } finally {
        this.humanAuditSaving = false;
      }
    },
  },
};
</script>

<style scoped src="./qa.css"></style>
